/**
 * StatTile + StatTileGrid — KPI tiles, codified from the inlined Triveny
 * pattern (ColdOutreachPage stat row): auto-fit minmax(180px,1fr) grid,
 * surface card, mono micro-label, Inter Tight value.
 */
import React from 'react'
import { Eyebrow } from './Eyebrow'

type StatTone = 'neutral' | 'accent' | 'success' | 'attention' | 'danger'

const toneClasses: Record<StatTone, string> = {
  neutral: 'bg-paper-2 text-ink-500',
  accent: 'bg-v2accent-soft text-v2accent-ink',
  success: 'bg-success-soft text-success-ink',
  attention: 'bg-attention-soft text-attention-ink',
  danger: 'bg-danger-soft text-ink-700',
}

interface StatTileProps {
  label: React.ReactNode
  value: React.ReactNode
  /** Optional small pill after the value (e.g. "+12%" or a status word). */
  delta?: React.ReactNode
  tone?: StatTone
  /** Optional caption under the value. */
  caption?: React.ReactNode
  className?: string
}

export function StatTile({
  label,
  value,
  delta,
  tone = 'neutral',
  caption,
  className = '',
}: StatTileProps) {
  return (
    <div
      className={`rounded-[10px] border border-v2border bg-surface px-4 py-3.5 shadow-v2-1 ${className}`}
    >
      <Eyebrow>{label}</Eyebrow>
      <div className="mt-1 flex items-baseline gap-2">
        <div className="font-display text-[22px] font-semibold tracking-[-0.015em] text-ink">
          {value}
        </div>
        {delta !== undefined && (
          <span
            className={`inline-flex h-5 items-center rounded-full px-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.03em] ${toneClasses[tone]}`}
          >
            {delta}
          </span>
        )}
      </div>
      {caption !== undefined && <div className="mt-0.5 text-[13px] text-ink-500">{caption}</div>}
    </div>
  )
}

export function StatTileGrid({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={`grid gap-3 ${className}`}
      style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
    >
      {children}
    </div>
  )
}
